import { Component, OnInit, Input, Output, OnChanges, EventEmitter } from '@angular/core';
import { FormControl } from '@angular/forms';
import * as d3 from 'd3';
import * as _ from 'lodash';
import * as Moment from 'moment-timezone';
import { extendMoment } from 'moment-range';
/** moment.js extended instance with timezone and moment-range */
const moment = extendMoment(Moment);

/**
 * CalendarRangeDatesComponent
 * Generates a component that allows to select year and month or trimester
 */
@Component({
  selector: 'app-calendar-range-dates',
  templateUrl: './calendar-range-dates.component.html',
  styleUrls: ['./calendar-range-dates.component.scss']
})
export class CalendarRangeDatesComponent implements OnInit, OnChanges {
  /** Two-positions array that specified date range for component */
  @Input() dateRange: string[];
  /** Selected range, defined by a start date and end date. Should be in original dateRange */
  @Input() selectedRange: { start: string, end: string };
  /** Emitter when dateStart has changed */
  @Output() dateStartChange = new EventEmitter<any>();
  /** Emitter when dateEnd has changed */
  @Output() dateEndChange = new EventEmitter<any>();
  /** Keeps the number of months existent in dateRange */
  monthsBetween: any;
  /** Keeps the frequency of specified dates (montly, yearly) */
  frequency: string;
  /** Years container in date range */
  years: number[];
  /** Months definition (with names, start date and end date) */
  months: { name: string, startDate: string, endDate: string }[];
  /** Selected year start of the form */
  selectedYear: FormControl;
  /** Selected year end of the form */
  selectedYearEnd: FormControl;
  /** Selected month start in the form */
  selectedMonth: { name: string, startDate: string, endDate: string };
  /** Selected month end in the form */
  selectedMonthEnd: { name: string, startDate: string, endDate: string };
  /** date of start */
  startDate: string;
  /** date of end */
  endDate: string;

  /** Init component, start selected year and selected end year Form Controls */
  constructor() {
    moment.locale('es-US');
    moment['tz'].setDefault('America/Mexico_City');
    this.selectedYear = new FormControl();
    this.selectedYearEnd = new FormControl();
  }

  /** Define years and months based on date range, start components with ranges */
  ngOnInit() {
    this.years = [];
    this.months = [];

    // Start year selected
    this.selectedYear.valueChanges.subscribe(val => {
      if (val && val !== (+moment(this.startDate).format('Y'))) {
        if (this.selectedMonth && this.selectedMonth.startDate && moment(val + this.selectedMonth.startDate).startOf('month').isValid()) {
          this.startDate = moment(val + this.selectedMonth.startDate).startOf('month').toISOString();
        } else if (this.selectedRange.start) {
          this.startDate = moment(val + '-' + moment(this.selectedRange.start).format('MM')).startOf('month').toISOString();
          this.findMonthStart();
        } else {
          this.startDate = moment(val + '-01').startOf('month').toISOString();
        }
        this.dateStartChange.emit(this.startDate);
        this.validateDates();
      }
    });
    // End year selected
    this.selectedYearEnd.valueChanges.subscribe(val => {
      if (val && val !== (+moment(this.endDate).format('Y'))) {
        if (this.selectedMonthEnd && this.selectedMonthEnd.endDate) {
          this.endDate = moment(val + this.selectedMonthEnd.endDate).endOf('month').toISOString();
        } else if (this.selectedRange.end) {
          this.endDate = moment(val + '-' + moment(this.selectedRange.end).format('MM')).endOf('month').toISOString();
          this.findMonthEnd();
        } else {
          this.endDate = moment(val + '-12-31').endOf('month').toISOString();
        }
        if (moment(this.endDate).toDate().getTime() < moment(this.startDate).toDate().getTime()) {
          this.validateDates();
        } else {
          this.dateEndChange.emit(this.endDate);
        }
      }
    });
  }

  /** Called when a month is selected in the form, updates dates selected */
  selectMonth(month) {
    this.selectedMonth = month;
    this.startDate = moment(this.selectedYear.value + this.selectedMonth.startDate).startOf('month').toISOString();
    this.selectedRange.start = this.startDate;
    this.dateStartChange.emit(this.startDate);
    this.validateDates();
  }

  /** Called when a month end is selected in the form, updates dates selected */
  selectMonthEnd(month) {
    this.selectedMonthEnd = month;
    this.endDate = moment(this.selectedYearEnd.value + month.endDate).endOf('month').toISOString();
    this.selectedRange.end = this.endDate;
    if (moment(this.endDate).toDate().getTime() < moment(this.startDate).toDate().getTime()) {
      this.validateDates();
    } else {
      this.dateEndChange.emit(this.endDate);
    }
  }

  /** Validates selected dates. End date should be after start date */
  validateDates() {
    if (moment(this.endDate).toDate().getTime() < moment(this.startDate).toDate().getTime()) {
      alert('La fecha final no puede ser anterior que la fecha inicial');
      this.selectedYearEnd.setValue(+moment(this.startDate).format('Y'));
      this.endDate = moment(this.startDate).endOf('month').toISOString();
      this.findMonthEnd();
      this.dateEndChange.emit(this.endDate);
    }
  }

  /**
   * Check if current month is included in dateRange
   * @param year Selected year
   * @param month Current month
   */
  isMonthDisabled(year: number, month: { name: string, startDate: string, endDate: string }) {
    if (!year) {
      return true;
    }
    const realDates = this.dateRange.filter(date => {
      return +moment(date.toString().slice(0, 10)).format('Y') === year;
    });

    if (realDates && realDates[0] && realDates.length < this.months.length) {
      const rangeOne = moment.range(moment(realDates[0].toString().slice(0, 10)),
        moment(realDates[realDates.length - 1].toString().slice(0, 10)));
      const rangeTwo = moment.range(moment(year + month.startDate).startOf('month'),
        moment(year + month.endDate).endOf('month'));

      return !rangeOne.intersect(rangeTwo);
    } else {
      return false;
    }
  }

  /**
   * Truggered when @Input() param changes
   * @param changes Changes object
   */
  ngOnChanges(changes) {
    const vm = this;
    if (changes.hasOwnProperty('dateRange')) {
      if (this.dateRange.length > 0) {
        this.dateRange = this.dateRange.filter(d => moment(d).isValid());
        this.monthsBetween = d3.min(this.dateRange, function (date: string, index: number) {
          if (date && moment(date).isValid() && vm.dateRange[index + 1] && moment(vm.dateRange[index + 1]).isValid()) {
            return Math.floor(moment(vm.dateRange[index + 1]).diff(date, 'days') / 28);
          } else {
            return 99999999;
          }
        });

        this.years = _.uniq(this.dateRange.filter(o => {
          return moment(o.toString().slice(0, 10)).isValid();
        }).map(o => {
          return +moment(o.toString().slice(0, 10)).format('Y');
        }));

        if (this.monthsBetween === 12) {
          this.frequency = 'Anual';
          this.months = [];
        } else if (this.monthsBetween === 3) {
          this.frequency = 'Trimestral';
          this.months = [
            { name: 'Ene - Mar', startDate: '-01', endDate: '-03' },
            { name: 'Abr - Jun', startDate: '-04', endDate: '-06' },
            { name: 'Jul - Sep', startDate: '-07', endDate: '-09' },
            { name: 'Oct - Dic', startDate: '-10', endDate: '-12' },
          ];
        } else if (this.monthsBetween === 1) {
          this.frequency = 'Mensual';
          this.months = [
            { name: 'Ene', startDate: '-01', endDate: '-01' },
            { name: 'Feb', startDate: '-02', endDate: '-02' },
            { name: 'Mar', startDate: '-03', endDate: '-03' },
            { name: 'Abr', startDate: '-04', endDate: '-04' },
            { name: 'May', startDate: '-05', endDate: '-05' },
            { name: 'Jun', startDate: '-06', endDate: '-06' },
            { name: 'Jul', startDate: '-07', endDate: '-07' },
            { name: 'Ago', startDate: '-08', endDate: '-08' },
            { name: 'Sep', startDate: '-09', endDate: '-09' },
            { name: 'Oct', startDate: '-10', endDate: '-10' },
            { name: 'Nov', startDate: '-11', endDate: '-11' },
            { name: 'Dic', startDate: '-12', endDate: '-12' },
          ];
        }
        this.setDefaultValues();
      }
    }
    if (changes.hasOwnProperty('selectedRange')) {
      this.setDefaultValues();
    }
  }

  /** Set default values based on selected range */
  setDefaultValues() {
    if (this.selectedRange) {
      this.startDate = this.selectedRange.start;
      this.selectedYear.setValue(+moment(this.startDate).format('Y'));
      this.findMonthStart();

      if (this.selectedRange.end) {
        this.endDate = this.selectedRange.end;
        this.selectedYearEnd.setValue(+moment(this.endDate).format('Y'));
        this.findMonthEnd();
      }
    }
  }

  /** Find month object definition based on start date */
  findMonthStart() {
    if (this.months) {
      this.selectedMonth = _.find(this.months, function (month) {
        const range = moment.range(moment(this.selectedYear.value + month.startDate).startOf('month'),
          moment(this.selectedYear.value + month.endDate).endOf('month'));
        if (range.contains(moment(this.startDate))) {
          return true;
        } else {
          return false;
        }
      }.bind(this));
    }
  }

  /** Find month end object definition based on end date */
  findMonthEnd() {
    if (this.months) {
      this.selectedMonthEnd = _.find(this.months, function (month) {
        const range = moment.range(moment(this.selectedYearEnd.value + month.startDate).startOf('month'),
          moment(this.selectedYearEnd.value + month.endDate).endOf('month'));
        if (range.contains(moment(this.endDate))) {
          return true;
        } else {
          return false;
        }
      }.bind(this));
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/components/calendar-range-dates/calendar-range-dates.component.ts